import { ComponentType, createElement } from 'react';

import { primitive } from 'styles/constants';

import type {
  TakeoverBindingRecord,
  TakeoverDesignToken,
  TakeoverManifest,
  TakeoverManifestAssetKey,
  TakeoverManifestComponentKey,
  TakeoverRuntimeReference,
  TakeoverTargetGroup,
} from './manifest-types';
import type { AppTakeoverConfig, TakeoverMenuSectionConfig, TakeoverPdpItemConfig } from './types';

const DESIGN_TOKEN_VALUES: Record<TakeoverDesignToken, string> = {
  $bbqBrown: primitive.$bbqBrown,
  $greyFive: primitive.$greyFive,
  $greyThree: primitive.$greyThree,
  $mayoEggWhite: primitive.$mayoEggWhite,
  $white: primitive.$white,
};

export interface TakeoverRuntimeBindings<Manifest extends TakeoverManifest> {
  assets: TakeoverBindingRecord<TakeoverManifestAssetKey<Manifest>, unknown>;
  components: TakeoverBindingRecord<TakeoverManifestComponentKey<Manifest>, ComponentType<any>>;
}

const isRuntimeReference = (value: unknown): value is TakeoverRuntimeReference => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<TakeoverRuntimeReference>;
  return (
    (candidate.kind === 'asset' || candidate.kind === 'component' || candidate.kind === 'token') &&
    typeof candidate.key === 'string'
  );
};

const hasOwn = (value: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const referenceError = (
  manifest: TakeoverManifest,
  kind: string,
  key: string,
  path: string,
  detail: string
): Error => new Error(`Takeover "${manifest.id}" ${kind} reference "${key}" at ${path} ${detail}.`);

const resolveValue = (
  manifest: TakeoverManifest,
  bindings: TakeoverRuntimeBindings<TakeoverManifest>,
  value: unknown,
  path: string
): unknown => {
  if (isRuntimeReference(value)) {
    if (value.kind === 'asset') {
      if (!hasOwn(manifest.assets, value.key)) {
        throw referenceError(manifest, 'asset', value.key, path, 'has no manifest.assets entry');
      }
      if (!hasOwn(bindings.assets, value.key)) {
        throw referenceError(manifest, 'asset', value.key, path, 'has no runtime binding');
      }
      return bindings.assets[value.key];
    }

    if (value.kind === 'token') {
      if (!hasOwn(DESIGN_TOKEN_VALUES, value.key)) {
        throw referenceError(manifest, 'token', value.key, path, 'is not approved');
      }
      return DESIGN_TOKEN_VALUES[value.key];
    }

    if (!hasOwn(bindings.components, value.key)) {
      throw referenceError(manifest, 'component', value.key, path, 'has no runtime binding');
    }
    const RuntimeComponent = bindings.components[value.key];
    if (value.props === undefined) {
      return RuntimeComponent;
    }
    const resolvedProps = resolveValue(manifest, bindings, value.props, `${path}.props`) as Record<
      string,
      unknown
    >;
    const BoundTakeoverComponent = (runtimeProps: Record<string, unknown>) =>
      createElement(RuntimeComponent, { ...runtimeProps, ...resolvedProps });
    BoundTakeoverComponent.displayName = `${manifest.id}:${value.key}`;
    return BoundTakeoverComponent;
  }

  if (Array.isArray(value)) {
    return value.map((item, index) => resolveValue(manifest, bindings, item, `${path}[${index}]`));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        resolveValue(manifest, bindings, child, `${path}.${key}`),
      ])
    );
  }

  return value;
};

const expandTargetGroups = (
  manifest: TakeoverManifest,
  bindings: TakeoverRuntimeBindings<TakeoverManifest>,
  groups: readonly TakeoverTargetGroup<unknown>[],
  path: string
): Record<string, unknown> => {
  const targetMap: Record<string, unknown> = {};
  groups.forEach((group, groupIndex) => {
    const resolvedConfig = resolveValue(
      manifest,
      bindings,
      group.config,
      `${path}[${groupIndex}].config`
    );
    group.ids.forEach(id => {
      if (hasOwn(targetMap, id)) {
        throw new Error(
          `Takeover "${manifest.id}" declares duplicate target ID "${id}" at ${path}.`
        );
      }
      targetMap[id] = resolvedConfig;
    });
  });
  return targetMap;
};

/** Build the legacy runtime contract from one authoritative declarative manifest. */
export const buildAppTakeoverConfig = <Manifest extends TakeoverManifest>(
  manifest: Manifest,
  bindings: TakeoverRuntimeBindings<Manifest>
): AppTakeoverConfig => {
  const runtimeBindings = bindings as TakeoverRuntimeBindings<TakeoverManifest>;
  const { menu, pdp, ...baseConfig } = manifest.config;
  const runtimeConfig = resolveValue(
    manifest,
    runtimeBindings,
    baseConfig,
    'config'
  ) as AppTakeoverConfig;

  if (menu) {
    const { targetSectionGroups, ...menuConfig } = menu;
    runtimeConfig.menu = {
      ...(resolveValue(manifest, runtimeBindings, menuConfig, 'config.menu') as Omit<
        NonNullable<AppTakeoverConfig['menu']>,
        'targetSectionIds'
      >),
      targetSectionIds: expandTargetGroups(
        manifest,
        runtimeBindings,
        targetSectionGroups,
        'config.menu.targetSectionGroups'
      ) as Record<string, TakeoverMenuSectionConfig>,
    };
  }

  if (pdp) {
    const { targetMenuItemGroups, ...pdpConfig } = pdp;
    runtimeConfig.pdp = {
      ...(resolveValue(manifest, runtimeBindings, pdpConfig, 'config.pdp') as Omit<
        NonNullable<AppTakeoverConfig['pdp']>,
        'targetMenuItemIds'
      >),
      targetMenuItemIds: expandTargetGroups(
        manifest,
        runtimeBindings,
        targetMenuItemGroups,
        'config.pdp.targetMenuItemGroups'
      ) as Record<string, TakeoverPdpItemConfig>,
    };
  }

  return runtimeConfig;
};
